import { Injectable } from "@angular/core";
import { Point, ScoutPath, ScoutPoint, ScoutPointEnvData, ScoutPointHorizontalError, ScoutPointRefData, ScoutPointVerticalError } from "../models";
import { ScoutTaskDoc } from "../schema/scout-task-schema";
import { extractPathNumberFromPathId, ScoutTaskService } from "./scout-task.service";

export type ScoutPointInfo = {
  scout: ScoutTaskDoc;
  scoutPath: ScoutPath;
  scoutPoint: ScoutPoint;
  pathId: string;
};

@Injectable({ providedIn: 'root' })
export class ScoutPointService {

  constructor( public scoutTaskService: ScoutTaskService){
  }

  pointsDiffer(lhsPts: ScoutPoint[], rhsPts: ScoutPoint[], bDeepCompare: boolean) {
    if (lhsPts.length !== rhsPts?.length) {
      console.log('length differs')
      return true;
    } else {
      const bPointDiffers = rhsPts.some((psp, index) => {
      const sp = lhsPts[index]
      return this.pointDiffers(sp,psp,bDeepCompare)
    })
    if (bPointDiffers) {
        return true;
    }
  }
    return false;
  }


  pointDiffers( lhs: ScoutPoint, rhs: ScoutPoint, bDeepCompare: boolean ) {
    if (lhs.id != rhs.id) {
      console.log('id differs')
      return true;
    } else if (lhs.parentId != rhs.parentId) {
      console.log('parentId differs')
      return true;
    } else if (lhs.type != rhs.type) {
      console.log('point type differs')
      return true;
    } else if (lhs.type == 'HA' && lhs.pointId != rhs.pointId) {
      console.log('HA point type differs')
      return true;
    } else {
      let fieldNames = ['bearing', 'horizontalDistance', 'heightOffset', 'exPointId', 'derivedFromExternalWall']
      if (lhs.type == 'HA' && lhs.pointId == Point.NewPointId) {
          fieldNames.push('latitude', 'longitude', 'altitudeEllipsoidMeters')
      }
      let bDiffers = fieldNames.some(fieldName => lhs[fieldName] != rhs[fieldName])
      if (bDiffers) {
          console.log(lhs.type + ' data differs: ' + fieldNames.filter(fieldName => lhs[fieldName] != rhs[fieldName]).join(', '))
      } else if (bDeepCompare) {
          bDiffers = this.refDataDiffer(lhs?.refData, rhs?.refData)
              || this.envDataDiffer(lhs?.envData, rhs?.envData)
              || this.horizontalErrorSettingsDiffer(lhs?.horizontalError, rhs?.horizontalError)
              || this.verticalErrorSettingsDiffer(lhs?.verticalError, rhs?.verticalError)
      }
      return bDiffers;
    }

  }


  refDataDiffer( lhs : ScoutPointRefData, rhs : ScoutPointRefData ) {
    if ( !lhs && !rhs ) {
      return false
    } else if ( !lhs || !rhs ) {
      return true
    }
    return lhs.location1Distance != rhs.location1Distance || lhs.location2Distance != rhs.location2Distance
  }


  envDataDiffer( lhs : ScoutPointEnvData, rhs : ScoutPointEnvData ) {
    if ( !lhs && !rhs ) {
      return false
    } else if ( !lhs || !rhs ) {
      return true
    }
    let fieldNames = ['pointDescription', 'bInterior', 'windowCount', 'clutterDescription', 'hvacDescription',
      'nearestWindowDistance', 'floorToCeilingHeight', 'quadrant']
    return fieldNames.some(fieldName => lhs[fieldName] != rhs[fieldName])
  }


  // Does not compare actual estimates because they change based on prior points in path
  horizontalErrorSettingsDiffer( lhs : ScoutPointHorizontalError, rhs : ScoutPointHorizontalError ) {
    if ( !lhs && !rhs ) {
      return false
    } else if ( !lhs || !rhs ) {
      return true
    }
    let fieldNames = ['implConstant','derivedFromExternalWall', 'externalWallAngleConstant', 'measuredAngleConstant']
    return fieldNames.some(fieldName => lhs[fieldName] != rhs[fieldName])
  }


  // Does not compare actual estimates because they change based on prior points in path
  verticalErrorSettingsDiffer( lhs : ScoutPointVerticalError, rhs : ScoutPointVerticalError ) {
    if ( !lhs && !rhs ) {
      return false
    } else if ( !lhs || !rhs ) {
      return true
    }
    let fieldNames = ['implConstant','unlevelFloor', 'floorVarianceConstant', 'unlevelFloorConstant']
    return fieldNames.some(fieldName => lhs[fieldName] != rhs[fieldName])
  }


  async buildScoutInfoForBuildingAndPath(args: {
    buildingId: string;
    pathId: string;
    stage: string;
  }) {
    const resultScoutInfos: ScoutPointInfo[] = [];
    let scoutsForBuilding = await this.scoutTaskService.rxCollection
      .find({
        selector: { 
          buildingId: args.buildingId
        }
      })
      .exec();
    if ( scoutsForBuilding.length == 0 ) {
        const msg = `No scouts for building ID "${args.buildingId}".`;
        console.warn(msg);
        this.scoutTaskService.publishError(msg,this,60);
        return resultScoutInfos;
    }
    let selectedScout = scoutsForBuilding[0];
    const pathNumber = extractPathNumberFromPathId(args.pathId);
    const scoutsForBuildingAndPath = scoutsForBuilding?.filter(st=>st.scoutPaths?.some(sp=>sp.pathNumber==pathNumber));
    if ( scoutsForBuildingAndPath.length === 0 ) {
        const msg = `No scouts for building ID "${args.buildingId} and test path ${args.pathId}.`;
        console.warn(msg);
        this.scoutTaskService.publishError(msg,this,60);
    } else {
      selectedScout = scoutsForBuildingAndPath[0];
      const scoutsForBuildingAndPathAndStage = scoutsForBuildingAndPath?.filter(st=>st.stage===args.stage);
      if ( scoutsForBuildingAndPathAndStage.length > 0 ) {
        selectedScout = scoutsForBuildingAndPathAndStage[0];
        if ( scoutsForBuildingAndPathAndStage.length > 1 ) {
          const msg = `Multiple scouts for building ID "${args.buildingId} and test path ${args.pathId} and stage ${args.stage} (using first match on stage).`;  
          console.warn(msg);
          this.scoutTaskService.publishError(msg,this,60);
        };
      }
    }
    const scoutPath = selectedScout?.scoutPaths.find((sp) => sp.pathNumber == pathNumber);
    if (scoutPath) {
      this.scoutTaskService.updateScoutPathContext(scoutPath, []);
    }
    scoutPath?.scoutPoints.forEach(scoutPoint=>{
      const scoutInfo = {
        scout: selectedScout,
        scoutPath,
        scoutPoint,
        pathId: args.pathId,
      }
      resultScoutInfos.push(scoutInfo);
    });
    return resultScoutInfos;
  }

}